<script setup lang="ts">
import type { ZulipConfig } from '@/utils/zulip/types';
import type {
  InitialData as HelpcenterSideBarChatFormInitialData,
  ValidData as HelpcenterSideBarChatFormValidData,
} from '@/components/forms/HelpcenterSideBarChatForm.vue';

import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useGoTo } from 'vuetify';

import { getPageContext } from '@/utils/Common';
import HelpcenterSideBarChatForm from '@/components/forms/HelpcenterSideBarChatForm.vue';
import HelpcenterSideBarChatMessagesVList from '@/components/vLists/HelpcenterSideBarChatMessagesVList.vue';
import { useZulip } from '@/composables/useZulip';
import { getTopicName } from '@/helpers/topic';

const config: ZulipConfig = {
  realm: import.meta.env.VITE_APP_ZULIP_REALM,
  username: import.meta.env.VITE_APP_ZULIP_USERNAME,
  apiKey: import.meta.env.VITE_APP_ZULIP_API_KEY,
};
const streamName = import.meta.env.VITE_APP_ZULIP_STREAM_NAME;
const actualTopic = getTopicName();

const {
  messages,
  error,
  sendMessage,
  initialize,
  cleanup,
  isLoading,
  loadMessages,
} = useZulip(config, streamName, actualTopic);

const goTo = useGoTo();

const chatInitialData = computed<HelpcenterSideBarChatFormInitialData>(() => {
  return {
    message: undefined,
    attachments: undefined,
  };
});

const handleSendMessage = async (
  formData: HelpcenterSideBarChatFormValidData,
): Promise<boolean> => {
  if (
    !formData.message.trim() &&
    Object.keys(formData.attachments).length === 0
  ) {
    return false;
  }

  // Add page context to the message
  const pageContext = getPageContext();
  let messageContent = formData.message;
  if (Object.keys(pageContext).length > 0) {
    messageContent += '';
    messageContent +=
      '\n' +
      '```spoiler Page Context\n' +
      JSON.stringify(pageContext, null, 2) +
      '\n```';
  }

  const success = await sendMessage(messageContent, formData.attachments);
  if (success) {
    await loadMessages(streamName, actualTopic);
    await goToBottom();
    return true;
  }
  return false;
};

watch(messages, (newMessages, oldMessages) => {
  if (
    newMessages[newMessages.length - 1]?.timestamp >
    (oldMessages[oldMessages.length - 1]?.timestamp ?? 0)
  ) {
    void goToBottom();
  }
});

onMounted(async () => {
  await initialize();
  await goToBottom();
});
onUnmounted(() => {
  cleanup();
});

async function goToBottom(): Promise<void> {
  await goTo('#end', {
    offset: 0,
    container: '#containerMessages',
  });
}
</script>

<template>
  <v-card v-if="!error" :class="$style.zulipChatWidget" elevation="2">
    <v-card-title class="bg-primary text-white"> Chat Support </v-card-title>

    <v-card-text id="containerMessages" :class="$style.messages" class="pa-0">
      <HelpcenterSideBarChatMessagesVList :messages="messages" />
      <div id="end" />
    </v-card-text>

    <v-divider></v-divider>

    <HelpcenterSideBarChatForm
      :loading="isLoading"
      :initial-data="chatInitialData"
      class="pa-4"
      @submit="handleSendMessage"
    />
  </v-card>

  <v-alert v-else type="error" class="ma-4">
    {{ error }}
  </v-alert>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.zulipChatWidget {
  height: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  max-height: 75vh; /* Ensure the container does not exceed the viewport height */
}

.messages {
  flex-grow: 1;
  overflow-y: auto;
}

</style>
